import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, Image, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MdArrowBack, MdShare, MdFavoriteBorder, MdPersonAdd, MdFlight, MdStyle, MdPeople, MdPerson } from 'react-icons/md';
import { SiYoutube, SiInstagram, SiFacebook, SiTiktok, SiX, SiXiaohongshu } from 'react-icons/si';
import { getAvatarSource, isPresetAvatar } from '@cubemeet/core/utils/avatarImageMapper';
import { getPresetAvatarAsset } from '@cubemeet/ui';
import { getUserPublicProfile, checkIsFollowing, followUser, unfollowUser, checkIsUserLiked, likeUser, unlikeUser } from '@cubemeet/core/services/userProfileService';
import { supabase } from '@cubemeet/core/services/supabase';
import useShowBackButton from '../shared/hooks/useShowBackButton';
import UserPosts from '../components/UserPosts';
import UserCards from '../components/UserCards';
import { MdFavorite } from 'react-icons/md';

interface UserProfilePageProps {}

/**
 * UserProfilePage - 用户公开资料页
 * 路由：/user/:id
 */
const UserProfilePage: React.FC<UserProfilePageProps> = () => {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const _search = typeof location?.search === 'string' ? location.search : (typeof window !== 'undefined' ? window.location.search : '');
  const _params = new URLSearchParams(_search);
  const refererParam = _params.get('referer') || _params.get('ref') || _params.get('referal') || _params.get('referrer') || _params.get('referral');
  const showBackInternal = useShowBackButton();
  const hasReferer = Boolean(refererParam)
    || (typeof document !== 'undefined' && !!document.referrer)
    || Boolean((location as any)?.state?.from)
    || showBackInternal;

  const [profile, setProfile] = useState<any>(null);
  const [counts, setCounts] = useState({ postsCount: 0, cardsCount: 0, tripsCount: 0, fansCount: 0, followingCount: 0, likeCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'posts' | 'cards'>('posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // 解析头像来源：优先使用预设头像资源，否则用网络URL
  const avatarSource = useMemo(() => {
    const url = profile?.avatar_url;
    if (!url) return { uri: 'https://via.placeholder.com/80' };
    if (isPresetAvatar(url)) {
      const preset = getPresetAvatarAsset(url);
      if (preset) return preset;
    }
    const source = getAvatarSource(url);
    return source || { uri: 'https://via.placeholder.com/80' };
  }, [profile?.avatar_url]);

  useEffect(() => {
    if (!userId) {
      setError('缺少用户 ID');
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchUserProfile = async () => {
      try {
        const result = await getUserPublicProfile(userId);

        if (!mounted) return;

        if (!result.profile) {
          setError('用户不存在或隐私设置不允许查看');
          setLoading(false);
          return;
        }

        setProfile(result.profile);
        setCounts({
          postsCount: result.postsCount,
          cardsCount: result.cardsCount,
          tripsCount: result.tripsCount,
          fansCount: result.fansCount || 0,
          followingCount: result.followingCount || 0,
          likeCount: result.likeCount || 0,
        });

        // Check auth and follow status
        const { data: { user } } = await supabase.auth.getUser();
        if (mounted) {
          setCurrentUser(user);
          if (user && user.id !== userId) {
            const [following, liked] = await Promise.all([
              checkIsFollowing(user.id, userId),
              checkIsUserLiked(user.id, userId)
            ]);
            if (mounted) {
              setIsFollowing(following);
              setIsLiked(liked);
            }
          }
        }
      } catch (err) {
        if (mounted) {
          console.error('Error fetching user profile:', err);
          setError('加载用户资料失败');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchUserProfile();

    return () => {
      mounted = false;
    };
  }, [userId]);

  const handleFollow = async () => {
    if (!currentUser) {
      window.alert('请先登录');
      return;
    }
    if (currentUser.id === userId) return;

    try {
      if (isFollowing) {
        await unfollowUser(currentUser.id, userId!);
        setIsFollowing(false);
        setCounts(prev => ({ ...prev, fansCount: Math.max(0, prev.fansCount - 1) }));
      } else {
        await followUser(currentUser.id, userId!);
        setIsFollowing(true);
        setCounts(prev => ({ ...prev, fansCount: prev.fansCount + 1 }));
      }
    } catch (err) {
      console.error('Follow action failed:', err);
      window.alert('操作失败，请重试');
    }
  };

  const handleLike = async () => {
    if (!currentUser) {
      window.alert('请先登录');
      return;
    }
    if (currentUser.id === userId) return;

    try {
      if (isLiked) {
        await unlikeUser(currentUser.id, userId!);
        setIsLiked(false);
        setCounts(prev => ({ ...prev, likeCount: Math.max(0, prev.likeCount - 1) }));
      } else {
        await likeUser(currentUser.id, userId!);
        setIsLiked(true);
        setCounts(prev => ({ ...prev, likeCount: prev.likeCount + 1 }));
      }
    } catch (err) {
      console.error('Like action failed:', err);
      window.alert('操作失败，请重试');
    }
  };

  const handleShare = async () => {
    if (!profile) return;
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const shareText = `${profile.display_name} - ${profile.bio || 'CubeMeet 用户'}`;
    try {
      if (navigator && (navigator as any).share) {
        await (navigator as any).share({ title: profile.display_name, text: shareText, url });
        return;
      }
    } catch (e) {
      // ignore and fallback to copy
    }
    if (navigator && (navigator as any).clipboard) {
      try {
        await (navigator as any).clipboard.writeText(url);
        window.alert('已复制页面链接');
        return;
      } catch (e) {
        // ignore
      }
    }
    // last resort
    window.prompt('复制此链接以分享', url);
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#ff6b35" />
        <Text style={styles.loadingText}>加载中...</Text>
      </View>
    );
  }

  if (error || !profile) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>{error || '用户不存在'}</Text>
        <TouchableOpacity onPress={() => navigate(-1)}>
          <Text style={styles.backLink}>← 返回</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.inner}>
      {/* 返回按钮 */}
      <View style={styles.header}>
        {hasReferer ? (
          <TouchableOpacity onPress={() => navigate(-1)}>
            <MdArrowBack size={24} color="#333" />
          </TouchableOpacity>
        ) : <View />}
        <TouchableOpacity onPress={handleShare}>
          <MdShare size={24} color="#333" />
        </TouchableOpacity>
      </View>

      {/* 用户头部信息 */}
      <View style={styles.profileSection}>
        <Image
          source={avatarSource}
          style={styles.avatar}
        />
        <View style={styles.profileInfo}>
          <Text style={styles.displayName}>{profile.display_name}</Text>
          {profile.bio && <Text style={styles.bio}>{profile.bio}</Text>}
        </View>

        {currentUser && currentUser.id !== userId && (
          <TouchableOpacity
            style={[styles.followButton, isFollowing && styles.followingButton]}
            onPress={handleFollow}
            activeOpacity={0.8}
          >
            <Text style={[styles.followButtonText, isFollowing && styles.followingButtonText]}>
              {isFollowing ? '已关注' : '关注'}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* 社交媒体图标行（仅图标，点击打开链接） */}
      {(() => {
        const socials = (profile?.social_accounts || {}) as Record<string, any>;
        const entries: Array<{ key: string; href: string; Icon: React.ComponentType<any>; color?: string }> = [];
        const addIf = (key: string, Icon: any, buildHref: (v: any) => string | null, color?: string) => {
          const v = (socials as any)[key];
          if (!v || typeof v !== 'object') return;
          const href = buildHref(v);
          if (href) entries.push({ key, href, Icon, color });
        };
        addIf('youtube', SiYoutube, (v) => v.url || (v.id ? `https://www.youtube.com/@${v.id}` : null), '#FF0000');
        addIf('instagram', SiInstagram, (v) => v.url || (v.id ? `https://www.instagram.com/${v.id}` : null));
        addIf('facebook', SiFacebook, (v) => v.url || (v.id ? `https://www.facebook.com/${v.id}` : null), '#1877F2');
        addIf('tiktok', SiTiktok, (v) => v.url || (v.id ? `https://www.tiktok.com/@${v.id}` : null));
        addIf('x', SiX, (v) => v.url || (v.id ? `https://x.com/${v.id}` : null));
        // 小红书：多为自定义链接，优先使用 url；无法稳定从 id 构造
        addIf('xiaohongshu', SiXiaohongshu, (v) => v.url || null, '#FE274E');

        if (entries.length === 0) return null;
        return (
          <View style={styles.socialRow}>
            {entries.map((it) => (
              <TouchableOpacity
                key={it.key}
                onPress={() => {
                  try { window.open(it.href, '_blank'); } catch {}
                }}
                style={styles.socialIconBtn}
                accessibilityRole="link"
              >
                <it.Icon size={18} color={it.color || '#333'} />
              </TouchableOpacity>
            ))}
          </View>
        );
      })()}

      {/* 互动按钮 */}
      <View style={styles.actionButtons}>
        <TouchableOpacity style={styles.actionButton} activeOpacity={0.7} onPress={handleLike}>
          {isLiked ? (
            <MdFavorite size={18} color="#ff6b35" />
          ) : (
            <MdFavoriteBorder size={18} color="#666" />
          )}
          <Text style={[styles.actionButtonText, isLiked && { color: '#ff6b35' }]}>
            点赞 {counts.likeCount > 0 ? counts.likeCount : ''}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} activeOpacity={0.7}>
          <MdPeople size={18} color="#666" />
          <Text style={styles.actionButtonText}>粉丝 {counts.fansCount}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} activeOpacity={0.7}>
          <MdPerson size={18} color="#666" />
          <Text style={styles.actionButtonText}>关注 {counts.followingCount}</Text>
        </TouchableOpacity>
      </View>

      {/* 标签页 */}
      <View style={styles.tabsSection}>
        <TouchableOpacity
          style={[styles.tabButton, activeTab === 'posts' && styles.tabButtonActive]}
          onPress={() => setActiveTab('posts')}
        >
          <MdFlight size={20} color={activeTab === 'posts' ? '#ff6b35' : '#999'} style={{ marginBottom: 4 }} />
          <Text style={[styles.tabButtonText, activeTab === 'posts' && styles.tabButtonTextActive]}>
            游记 {counts.postsCount}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tabButton, activeTab === 'cards' && styles.tabButtonActive]}
          onPress={() => setActiveTab('cards')}
        >
          <MdStyle size={20} color={activeTab === 'cards' ? '#ff6b35' : '#999'} style={{ marginBottom: 4 }} />
          <Text style={[styles.tabButtonText, activeTab === 'cards' && styles.tabButtonTextActive]}>
            卡片 {counts.cardsCount}
          </Text>
        </TouchableOpacity>
      </View>

      {/* 内容区 */}
      <View style={styles.contentSection}>
        {activeTab === 'posts' && <UserPosts userId={userId!} />}
        {activeTab === 'cards' && <UserCards userId={userId!} />}
      </View>
    </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f6f7f8',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  errorText: {
    fontSize: 16,
    color: '#ff4444',
    marginBottom: 16,
    textAlign: 'center',
  },
  backLink: {
    fontSize: 14,
    color: '#ff6b35',
    textDecorationLine: 'underline',
  },

  // ===== 头部 =====
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },

  // ===== 用户资料区 =====
  profileSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  socialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  socialIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#f0f0f0',
  },
  profileInfo: {
    flex: 1,
  },
  displayName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  bio: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
  },
  followButton: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: '#ff6b35',
    borderRadius: 20,
    marginLeft: 4,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  followingButton: {
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  followButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  followingButtonText: {
    color: '#999',
  },

  // ===== 互动按钮 =====
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },

  // ===== 标签页 =====
  tabsSection: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 8,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabButtonActive: {
    borderBottomColor: '#ff6b35',
  },
  tabButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#999',
  },
  tabButtonTextActive: {
    color: '#ff6b35',
  },

  // ===== 内容区 =====
  contentSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingBottom: 48,
  },

  // ===== 底部间距 =====
  bottomPadding: {
    height: 120,
  },
  inner: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    marginLeft: 'auto' as any,
    marginRight: 'auto' as any,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 6px 18px rgba(0,0,0,0.07)',
  },
});

export default UserProfilePage;
